
import { ErrorBoundary, withProfiler } from '@sentry/react'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import { useDebounce, useSentryUnblock } from '../hooks'
import { useAppSelector } from '../store'

import { AlertsModal } from './alerts/modal'
import { AuthStateListener } from './auth'
import { ErrorBoundaryFallback } from './error-boundary'
import { signinFromCognitoSession } from './LoginForm/actions'
import { Router } from './router'

function App (): JSX.Element | null {
  let dispatch = useDispatch()

  let [ isInitial, setIsInitial ] = useState(true)
  let [ isLoading, setIsLoading ] = useState(false)
  let [ error, setError ] = useState<Error>()

  useSentryUnblock()

  useEffect(() => {
    async function initialSetup (): Promise<void> {
      try {
        await signinFromCognitoSession(dispatch)
      } catch (err) {
        if (err instanceof Error) {
          if (isErrorWithCode(err) && [ 'ERR_NO_CURRENT_USER', 'NotAuthorizedException' ].includes(err.code)) {
            return
          }

          setError(err)
          return
        }

        throw err
      }
    }

    setIsLoading(true)
    setIsInitial(false)
    setError(undefined)

    initialSetup()
      .finally(() => {
        setIsLoading(false)
      })
  }, [ dispatch ])

  let debouncedIsLoading = useDebounce(isLoading, isLoading ? 750 : 0)

  if (isInitial) {
    return null
  }

  if (debouncedIsLoading) {
    return (
      <div className='tailwind'>
        <div className='w-full h-full min-h-screen flex items-center justify-center text-base p-8'>
          <div className='animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500' />
        </div>
      </div>
    )
  }

  if (isLoading) {
    return null
  }

  if (error) {
    return (
      <ErrorBoundaryFallback
        error={error}
      />
    )
  }
  // ToDo: find better place for Alerts Modal
  return (
    <>
      <AlertsModal />
      <AuthStateListener />
      <Router />
    </>
  )
}

function isErrorWithCode (error: unknown): error is Error & { code: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    typeof (error as { code: unknown }).code === 'string'
  )
}

function AppWithErrorBoundary (): JSX.Element {
  let profile = useAppSelector(state => state.auth.profile)

  return (
    <ErrorBoundary
      fallback={ErrorBoundaryFallback}
      showDialog
      dialogOptions={{
        user: {
          email: profile?.email?.trim()?.toLowerCase() ?? undefined,
          name: [ profile?.given_name?.trim(), profile?.family_name?.trim() ].filter(Boolean).join(' ') ?? undefined
        }
      }}
    >
      <App />
    </ErrorBoundary>
  )
}

export default withProfiler(AppWithErrorBoundary)
